/**
 * Hospora sign-in helpers for product frontends.
 *
 * Adapted from hospora/sdk/hospora-launch.ts. The one difference: there is NO default organisation. The
 * upstream copy still defaults VITE_HOSPORA_ORG to `jb-and-sons`, which would send another organisation's
 * staff to JB & Sons' sign-in; here the organisation must be named, by either
 *   - VITE_HOSPORA_ORG (build time, wins when set), or
 *   - the API, which is bound to its organisation (GET /config -> org.slug): `setHosporaOrg()`.
 * With neither, `hosporaOrg()` throws, loudly, instead of guessing.
 *
 * VITE_HOSPORA_URL  defaults to https://hospora.app
 * VITE_HOSPORA_ORG  the Hospora organisation (slug or id) this deployment serves (optional: the API says)
 */

export const HOSPORA_URL = (import.meta.env.VITE_HOSPORA_URL || 'https://hospora.app').replace(/\/+$/, '')
import { sharedOrgSlug } from './runtime'

const configured = (import.meta.env.VITE_HOSPORA_ORG ?? '').trim()
let resolved = configured

export class HosporaOrgMissingError extends Error {
  constructor() {
    super('This app does not know which organisation it serves: set VITE_HOSPORA_ORG, or point VITE_API_URL at an API that is bound to one.')
    this.name = 'HosporaOrgMissingError'
  }
}

/** The organisation named by the API, used only when VITE_HOSPORA_ORG is not set. */
export function setHosporaOrg(slug: string | null | undefined): void {
  if (!configured && slug?.trim()) resolved = slug.trim()
}

/** Test hook. */
export function _resetHosporaOrg(): void {
  resolved = configured
}

/** The organisation this deployment serves. Throws HosporaOrgMissingError when nothing says. */
export function hosporaOrg(): string {
  const pathOrg = sharedOrgSlug()
  if (pathOrg) return pathOrg
  if (!resolved) throw new HosporaOrgMissingError()
  return resolved
}

/** Where "Sign in with Hospora" sends the user; Hospora bounces back here with a token. */
export function hosporaLaunchUrl(product: string): string {
  return `${HOSPORA_URL}/launch/${encodeURIComponent(hosporaOrg())}/${encodeURIComponent(product)}`
}

/** Read `#hospora_token=…` from the URL and remove it from the address bar and history. */
export function takeHosporaToken(): string | null {
  const match = /(?:^#|&)hospora_token=([^&]+)/.exec(window.location.hash)
  if (!match?.[1]) return null
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
  try {
    return decodeURIComponent(match[1])
  } catch {
    return null
  }
}
